/**
 * <s-product-image-zoom> — PDP image zoom preloader and navigator.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus so the fullscreen
 * dialog has the high-resolution image ready when opened, and steps between zoom
 * dialogs with previous/next buttons and the arrow keys.
 *
 * Navigation follows the gallery triggers currently in the DOM, so it respects
 * variant-filtered galleries after s-variant-picker.js re-renders the partial.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 *   dialog [data-zoom-media]        — expanded video wrapper (videos join navigation too).
 *   dialog [data-zoom-nav]          — optional buttons with command="--zoom-previous" or "--zoom-next".
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    // CommandEvent does not bubble, so listen in the capture phase.
    this.addEventListener('command', this.#handleCommand, { ...opts, capture: true });
    this.addEventListener('keydown', this.#handleKeydown, opts);
    // Hide navigation when the current variant only shows one image.
    this.addEventListener('toggle', this.#handleToggle, { ...opts, capture: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #handleCommand = (event) => {
    if (event.command !== '--zoom-previous' && event.command !== '--zoom-next') return;
    if (!(event.target instanceof HTMLDialogElement)) return;

    event.preventDefault();
    this.#step(event.target, event.command === '--zoom-next' ? 1 : -1, event.command);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (!(event.target instanceof Element)) return;

    const dialog = event.target.closest('dialog[open]');
    if (!dialog || !this.contains(dialog) || !dialog.querySelector('img[data-zoom-image], [data-zoom-media]')) return;
    if (event.target instanceof HTMLMediaElement) return;

    const rtl = getComputedStyle(dialog).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== rtl;
    event.preventDefault();
    this.#step(dialog, forward ? 1 : -1, forward ? '--zoom-next' : '--zoom-previous');
  };

  #handleToggle = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;

    // Stop expanded video when the viewer closes or moves on; stop the page video while expanded.
    if (!dialog.open) {
      for (const video of dialog.querySelectorAll('video')) video.pause();
      return;
    }
    for (const video of this.querySelectorAll('[data-product-gallery] video')) video.pause();

    // Autoplay expanded video, always muted.
    for (const video of dialog.querySelectorAll('[data-zoom-media] video')) {
      video.muted = true;
      video.play().catch(() => {});
    }

    const single = this.#triggers().length < 2;
    for (const button of dialog.querySelectorAll('[data-zoom-nav]')) {
      button.hidden = single;
    }
  };

  #triggers() {
    return [...this.querySelectorAll('[data-product-gallery] [data-zoom-preload][commandfor]')];
  }

  #step(currentDialog, direction, command) {
    const triggers = this.#triggers();
    if (triggers.length < 2) return;

    const index = triggers.findIndex((trigger) => trigger.getAttribute('commandfor') === currentDialog.id);
    const nextIndex = index === -1 ? 0 : (index + direction + triggers.length) % triggers.length;
    const nextTrigger = triggers[nextIndex];
    const nextDialog = document.getElementById(nextTrigger.getAttribute('commandfor'));
    if (!(nextDialog instanceof HTMLDialogElement) || nextDialog === currentDialog) return;

    this.#preload(nextTrigger);
    currentDialog.close();
    // Focus the matching gallery image first so closing the new dialog returns focus there.
    nextTrigger.focus({ preventScroll: true });
    nextDialog.showModal();

    const sameButton = nextDialog.querySelector(`[data-zoom-nav][command="${command}"]`);
    if (sameButton instanceof HTMLElement && !sameButton.hidden) sameButton.focus({ preventScroll: true });

    // Warm the image after this one too.
    const afterTrigger = triggers[(nextIndex + direction + triggers.length) % triggers.length];
    if (afterTrigger) this.#preload(afterTrigger);
  }

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
